import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { ISalesDocument as ContractSalesDocument, SalesDocumentManagementService, Order } from '@cemex-core/cmx-api';
import {
  IDocumentItem,
  ISalesDocument,
  ISalesDocumentsRes,
  ISalesDocumentsReq,
} from '@cemex-core/cmx-api/models/sales-document-management';
import { createSelector, select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ContractBalance } from '@ordersnproducts/modules/order-builder/modules/specification/models/contract-balance';
import { getAvailableContractsBalance } from '@ordersnproducts/modules/order-builder/modules/specification/selectors/contract.selector';
import { SelectOptionI } from '@ordersnproducts/modules/shared/modules/configurable-form/components/form-multiselect/models/select-option-i';
import { AbstractField } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/abstract-field';
import { FieldConfig } from '@ordersnproducts/modules/shared/modules/configurable-form/models/field-configs/field-config.interface';
import { tonUnitConverter } from '@ordersnproducts/modules/shared/utils/api-data-correction';
import { loadAvailableContracts } from '@ordersnproducts/modules/shared/utils/contracts-with-balance-loader.helper';
import { Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  OneClickLoadInvoicesCompleteAction,
  OneClickSelectContractTypeAction,
} from '../../actions/one-click-contract.actions';
import { State } from '../../reducers';
import {
  getOneClickContractsAvailable,
  getOneClickContractTypeSelected,
} from '../../selectors/one-click-contract.selector';
import { getOneClickSelectedOrder } from '../../selectors/one-click-orders.selector';
import { setContractInOrder } from './contract.setter';
import { setInvoiceInOrder } from './invoice-agreement.setter';
import { setContractPaymentInOrder } from './payment.setter';
import { OneClickContractType } from '../../models/one-click-contract.model';
import { SALES_DOCUMENT_TYPE_ID } from '@ordersnproducts/modules/shared/constants/sales-document-type-id';

const contractsAndBalance = (index: number) =>
  createSelector(getOneClickContractsAvailable(index), getAvailableContractsBalance, (contracts, balance) => ({
    contracts,
    balance,
  }));

/**
 * Used to display or enter text in a form
 */
@Component({
  selector: 'app-contract-invoice-select',
  styleUrls: ['contract-invoice-select.component.scss'],
  templateUrl: 'contract-invoice-select.component.html',
})
export class MultipleContractTypeSelectorComponent extends AbstractField implements OnInit, OnDestroy {
  ContractType = OneClickContractType;
  contractTypeSelected: OneClickContractType;

  config: FieldConfig;
  group: UntypedFormGroup;

  allInvoiceDocuments: ISalesDocument[] = [];
  availableInvoiceDocuments: ISalesDocument[] = [];
  selectedInvoiceDocument: ISalesDocument;
  allContracts: {
    contracts: ContractSalesDocument[];
    balance: {
      [salesDocumentItemId: number]: ContractBalance;
    };
  };
  availableContracts: SelectOptionI[] = [];
  selectedContract: number;

  labels = {
    remaining: '',
    balance: 0,
  };

  private quantity: number;
  private ngDestroy = new Subject();

  constructor(
    private store: Store<State>,
    private translate: TranslateService,
    private salesDocumentsService: SalesDocumentManagementService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.listenToContractType();
    this.fetchQuantity();
    this.fetchInvoices();
    this.loadContracts();

    // Default to contract type
    this.store.dispatch(new OneClickSelectContractTypeAction(OneClickContractType.Contract, this.config['at']));
  }

  /**
   * Listens to selected contract type changes
   */
  private listenToContractType() {
    this.store
      .pipe(select(getOneClickContractTypeSelected), takeUntil(this.ngDestroy))
      .subscribe((contractTypeSelected) => {
        this.contractTypeSelected = contractTypeSelected[this.config['at']];
      });
  }

  /**
   * Listens to quantity changes
   */
  private fetchQuantity() {
    this.store
      .select(getOneClickSelectedOrder)
      .pipe(
        takeUntil(this.ngDestroy),
        filter((order) => !!order),
        map((order) => order.items[this.config['at']].quantity),
        filter((quantity) => this.quantity !== quantity),
        map((quantity) => (quantity === undefined ? 0 : quantity)),
        tap((quantity) => (this.quantity = quantity)),
      )
      .subscribe((quantity) => {
        // Reset
        this.selectedInvoiceDocument = undefined;
        this.selectedContract = undefined;
        this.filterInvoicesByBalance(quantity);
        this.filterContractsByBalance(quantity);
      });
  }

  /**
   * Loads available contracts
   */
  private loadContracts() {
    this.store
      .pipe(select(contractsAndBalance(this.config['at'])), takeUntil(this.ngDestroy))
      .subscribe((contractOptions) => {
        this.allContracts = contractOptions;
        this.filterContractsByBalance(this.quantity);
      });
  }

  /**
   * Loads available invoices
   */
  private fetchInvoices() {
    this.getInvoices()
      .pipe(
        take(1),
        map((response: ISalesDocumentsRes) => response.salesDocument),
        tap((invoiceDocuments) =>
          this.store.dispatch(new OneClickLoadInvoicesCompleteAction(invoiceDocuments, this.config['at'])),
        ),
        map((invoiceDocuments) =>
          invoiceDocuments.map((document) => ({
            ...document,
            name: document.salesDocumentCode,
          })),
        ),
      )
      .subscribe((invoiceDocuments) => {
        this.allInvoiceDocuments = invoiceDocuments;
        this.filterInvoicesByBalance(this.quantity);
      });
  }

  /**
   * Radio buttons options
   * @param contractType
   */
  public selectContractType(contractType: OneClickContractType) {
    // Reset
    this.selectedInvoiceDocument = undefined;
    this.selectedContract = undefined;
    setContractInOrder(undefined, this.store, this.config['at']);
    setContractPaymentInOrder(undefined, this.store, this.config['at']);
    setInvoiceInOrder(undefined, this.store, this.config['at']);

    this.store.dispatch(new OneClickSelectContractTypeAction(contractType, this.config['at']));
  }

  /**
   * Fires when contract selected changes
   * @param contractId
   */
  public contractChanged(contractId: number) {
    this.selectedInvoiceDocument = undefined;
    this.selectedContract = contractId;
    setContractInOrder(contractId, this.store, this.config['at']);
    setContractPaymentInOrder(contractId, this.store, this.config['at']);
  }

  /**
   * Invoice select event
   * @param invoiceDocument
   */
  public invoiceDocumentChanged(invoiceDocument: ISalesDocument) {
    this.selectedContract = undefined;
    this.selectedInvoiceDocument = invoiceDocument;
    if (invoiceDocument) {
      const invoice = invoiceDocument.documentItems[0];
      setContractInOrder(invoice.itemId, this.store, this.config['at']);
      setInvoiceInOrder(invoiceDocument, this.store, this.config['at']);
      this.labels.balance = this.invoiceBalance(invoice);
      this.labels.remaining = invoice.openedQuantity + ' ' + tonUnitConverter(invoice.unitOfMeasure.unitCode);
    } else {
      setContractInOrder(undefined, this.store, this.config['at']);
      setInvoiceInOrder(undefined, this.store, this.config['at']);
    }
  }

  /**
   * Filters invoices with remaining volume available
   * @param balance
   */
  private filterInvoicesByBalance(balance: number) {
    this.availableInvoiceDocuments = this.allInvoiceDocuments.filter(
      (document) => parseFloat(document.documentItems[0].openedQuantity) >= balance,
    );
  }

  /**
   * Filters contracts with remaining volume available
   * @param balance
   */
  private filterContractsByBalance(balance: number) {
    if (this.allContracts && this.allContracts.contracts) {
      const filteredContracts = this.allContracts.contracts.filter((contract) => {
        const contractBalance = this.getContractBalance(contract);
        return contractBalance && contractBalance.amount >= balance;
      });

      this.availableContracts = loadAvailableContracts(filteredContracts, this.allContracts.balance, this.translate);
    }
  }

  /**
   * Looks for the balance of give contract
   * @param contract
   */
  private getContractBalance(contract: ContractSalesDocument) {
    return this.allContracts.balance[contract.salesDocumentItemId];
  }

  /**
   * Retrieve invoice balance
   * @param invoice
   */
  private invoiceBalance(invoice: IDocumentItem): number {
    return 100 - (Number(invoice.appliedQuantity) * 100) / invoice.quantity;
  }

  /**
   * Fetches invoices available
   */
  private getInvoices() {
    return this.store.select(getOneClickSelectedOrder).pipe(
      take(1),
      switchMap((order) =>
        this.salesDocumentsService.getSalesDocumentsByProductId(
          order.items[this.config['at']].product.productId,
          this.makeSalesDocumentRequest(order),
        ),
      ),
    );
  }

  private makeSalesDocumentRequest(order: Order): ISalesDocumentsReq {
    return {
      salesDocumentTypeId: SALES_DOCUMENT_TYPE_ID.CONTRACT,
      jobSiteId: order.jobsite.jobsiteId,
      productLineId: order.items[this.config['at']].product.productType.productTypeId,
      contractType: OneClickContractType.ZCA,
      shippingConditionId: order.shippingCondition.shippingConditionId,
    } as ISalesDocumentsReq;
  }

  ngOnDestroy(): void {
    this.ngDestroy.next();
    this.ngDestroy.complete();
  }
}
